import {computed, makeObservable, observable, runInAction} from "mobx";
import {GameInfo} from "../games/game-info";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {ServiceBase} from "../ServiceBase";
import {ISlideRenderer, IVideoSlideStoredData, IVideoSlidesService} from "./video-slides.service.interface";
import {ApiListType, ArenaConfiguration, IVideoSlidesFilter} from "../configurations/config-data-sections/arenas.configuration";
import {VIDEO_SLIDES_ARENA} from "../arenas/arena-consts";
import {LocalStorageKeys} from "../storage/local-storage-keys";
import {VideoSlidesFilterModel} from "./models/video-slides-filter-editor/video-slides-filter.model";
import {ForYouVideoSlidesFilter} from "./models/video-slides-filter-editor/for-you-video-slides-filter";
import {SlidesRulesHandler} from "./models/rules/slides-rules-handler";
import React from "react";

const SLIDES_TO_CACHE = 4;

export class VideoSlidesService extends ServiceBase implements IVideoSlidesService {
    constructor(services: IServiceFactoryExtended) {
        super(services);


        this._videoSlidesFilterEditorList = this._createVideoSlidesFilters();
        this._loadGameTypes();

        makeObservable(this);
        this.reaction(() => this.currentAppliedFilter, () => {
            this._currentSlideIndex = this._getCurrentIndexFromLocalStorage();
        }, {
            fireImmediately: true
        })
    }



    private _gamesIdsWithStaticVideos: number[] = [];
    private _gamesIdsWithLiveStreamVideos: number[] = [];
    private _gamesIdsWithStaticVideosFromCDN: number[] = [];



    private get config(): ArenaConfiguration {
        return this.services.configuration.data.arenas[VIDEO_SLIDES_ARENA];
    }


    private _getGamesInfo(gameTypes: number[], condition: (gameInfo: GameInfo) => boolean): GameInfo[] {
        const gamesMap = this.services.games.gamesMap;
        return gameTypes.reduce((accumulator, currentGameType) => {
            const gameInfo = gamesMap[currentGameType];
            if(gameInfo && condition(gameInfo)) {
                accumulator.push(gameInfo);
            }
            return accumulator;
        }, []);
    }


    @computed
    private get gamesWithStaticVideos(): GameInfo[] {
        return this._getGamesInfo(this._gamesIdsWithStaticVideos, gameInfo => Boolean(gameInfo.videoSlideUrl));
    }

    @computed
    private get gamesWithLiveStreamVideos(): GameInfo[] {
        return this._getGamesInfo(this._gamesIdsWithLiveStreamVideos, gameInfo => Boolean(gameInfo.videoURL));
    }

    @computed
    get allGamesInArena(): GameInfo[] {
        return [
            ...this.gamesWithStaticVideos,
            ...this.gamesWithLiveStreamVideos
        ];
    }


    private _filterGamesInfo(gamesInfo: GameInfo[]): GameInfo[] {
        const filteredGamesMap = this.services.gamesFilter.filteredGamesMap;
        return gamesInfo.filter(gInfo => Boolean(filteredGamesMap[gInfo.gameType]));
    }


    @computed
    get filteredSlides(): ISlideRenderer[] {
        const gamesWithStaticVideos = this._filterGamesInfo(this.gamesWithStaticVideos);
        const gamesWithLiveStreamVideos = this._filterGamesInfo(this.gamesWithLiveStreamVideos);
        const rulesHandler = new SlidesRulesHandler(this.services, this.config.slidesRules, gamesWithStaticVideos, gamesWithLiveStreamVideos);
        return rulesHandler.createRenderers();
    }

    private _slidesToRender: ISlideRenderer[] = [];
    @computed
    get slidesToRender(): ISlideRenderer[] {

        if(this.filteredSlides.length === 0) {
            return [];
        }

        //when filters are applied the number of slidesToRender is bigger thant filteredSlides so we need to remove the extra slides wrappers
        const diff = this._slidesToRender.length - this.filteredSlides.length;
        if(diff > 0) {
            this._slidesToRender.splice(this.filteredSlides.length - 1, diff + 1);
            return this._slidesToRender;
        }

        if(this.currentSlideIndex <= this._slidesToRender.length - SLIDES_TO_CACHE) {
            return this._slidesToRender;
        }

        let slidesToAdd = this.currentSlideIndex - this._slidesToRender.length + SLIDES_TO_CACHE + 1;
        for(let i = 1; i <= slidesToAdd; i++) {
            this._slidesToRender.push(new SlideRendererWrapper(this._slidesToRender.length, this, this.services.tools.getGuid()));
        }

        return this._slidesToRender;
    }

    private readonly _videoSlidesFilterEditorList: VideoSlidesFilterModel[] = [];
    get videoSlidesFilters(): VideoSlidesFilterModel[] {
        return this._videoSlidesFilterEditorList;
    }

    @computed
    private get currentAppliedFilter(): VideoSlidesFilterModel {
        return this.videoSlidesFilters.find(f => f.on);
    }


    private _getCurrentIndexFromLocalStorage(): number {


        const currentFilter = this.currentAppliedFilter;
        if(!currentFilter) {
            return 0;
        }

        const lastIndexPerFilter = this.services.localStorage.getJson<IVideoSlideStoredData>(LocalStorageKeys.videoSlides)?.lastIndexPerFilter ?? {};

        return lastIndexPerFilter[currentFilter.id] ?? 0;
    }


    @observable.ref
    private _currentSlideIndex: number = 0;

    get currentSlideIndex(): number {
        return this._currentSlideIndex
    }

    set currentSlideIndex(value: number) {
        runInAction(() => {
            this._currentSlideIndex = value;
        });

        const currentStorageData = this.services.localStorage.getJson<IVideoSlideStoredData>(LocalStorageKeys.videoSlides) ?? {
            lastIndexPerFilter: {}
        }

        const filter = this.currentAppliedFilter;
        if(filter) {
            currentStorageData.lastIndexPerFilter[filter.id] = value;
        }

        this.services.localStorage.setJson(LocalStorageKeys.videoSlides, currentStorageData);
    }

    private _createVideoSlidesFilters(): VideoSlidesFilterModel[] {
        const filters: IVideoSlidesFilter[] = this.services.configuration.data.arenas[VIDEO_SLIDES_ARENA].filters;

        return [
            new ForYouVideoSlidesFilter(this.services, {id: "forYou", category: "forYou", translationKey: "For You", icon: "forYouIcon"}),
            ...filters.map((filter) => new VideoSlidesFilterModel(this.services, filter))
        ];
    }

    private _loadGameTypes(): void {
        this.config.swiperGamesList?.forEach(swiperList => {
            switch (swiperList.apiListType) {
                case ApiListType.STATIC_VIDEO:
                    this._gamesIdsWithStaticVideosFromCDN = swiperList.gamesId;
                    break;
                case ApiListType.LIVE_CASINO_VIDEO:
                    this._gamesIdsWithLiveStreamVideos = swiperList.gamesId;
                    break;
                default:
                    //this will be changed to a dedicated new API List type
                    this._gamesIdsWithStaticVideos = swiperList.gamesId;
                    break;
            }
        });
    }
}


class SlideRendererWrapper implements ISlideRenderer {
    constructor(private readonly index: number,
                private readonly videoSlideService: VideoSlidesService,
                private readonly _tempId: string) {
    }

    private get originalRenderer(): ISlideRenderer | undefined {
        return this.videoSlideService.filteredSlides[this.index];
    }

    get uniqueKey(): string {
        return this.originalRenderer?.uniqueKey ?? this._tempId;
    }

    render(): React.ReactElement {
        const currentIndex = this.videoSlideService.currentSlideIndex;
        if(this.index >= currentIndex - SLIDES_TO_CACHE && this.index <= currentIndex + SLIDES_TO_CACHE) {
            return this.originalRenderer?.render();
        } else {
            return (
                <div></div>
            )
        }

    }
}